import { z } from 'zod';

export const AuthenticationStatusSchema = z.enum([
  'AUTHENTICATED',
  'UNAUTHENTICATED',
]);

export const AuthenticationDetailsStreamOutputSchema = z.object({
  username: z.string().nullable(),
  authenticationStatus: AuthenticationStatusSchema,
});

export type AuthenticationDetailsStreamOutput = z.infer<
  typeof AuthenticationDetailsStreamOutputSchema
>;

export const parseAuthenticationDetailsStreamOutput = (
  value: unknown,
): AuthenticationDetailsStreamOutput | null => {
  if (typeof value === 'string') {
    try {
      return AuthenticationDetailsStreamOutputSchema.parse(JSON.parse(value));
    } catch {
      return null;
    }
  }

  const parsed = AuthenticationDetailsStreamOutputSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

export const formatAuthenticationStatusLabel = (
  authenticationStatus: AuthenticationDetailsStreamOutput['authenticationStatus'],
): string => {
  if (authenticationStatus === 'AUTHENTICATED') {
    return 'Authenticated';
  }

  if (authenticationStatus === 'UNAUTHENTICATED') {
    return 'Unauthenticated';
  }

  return authenticationStatus;
};

export const formatAuthenticationDetailsSummary = ({
  username,
  authenticationStatus,
}: AuthenticationDetailsStreamOutput): string =>
  [username, formatAuthenticationStatusLabel(authenticationStatus)]
    .filter(Boolean)
    .join(' · ');

export const buildAuthenticationDetailsStreamPayload = ({
  username,
  isAuthenticated,
}: {
  username?: string | null;
  isAuthenticated: boolean;
}): { labelPast: string; output: string } => {
  const displayUsername = username?.trim() || 'Unknown user';
  const authenticationStatus: AuthenticationDetailsStreamOutput['authenticationStatus'] =
    isAuthenticated ? 'AUTHENTICATED' : 'UNAUTHENTICATED';
  const streamOutput: AuthenticationDetailsStreamOutput = {
    username: username ?? null,
    authenticationStatus,
  };

  return {
    labelPast: `${displayUsername} · ${formatAuthenticationStatusLabel(authenticationStatus)}`,
    output: JSON.stringify(streamOutput),
  };
};
